// Layout Theme System - Light / Regular (brand) / Dark
export const THEME_MODES = ['light', 'regular', 'dark'];

let _activeThemeMode = 'light';

export const setActiveThemeMode = (mode) => {
  _activeThemeMode = THEME_MODES.includes(mode) ? mode : 'light';
};

export const getActiveThemeMode = () => _activeThemeMode;

export const normalizeThemeMode = (value) => {
  if (value === true || value === 'dark') return 'dark';
  if (value === 'regular') return 'regular';
  if (value === false || value === 'light') return 'light';
  if (THEME_MODES.includes(value)) return value;
  return 'light';
};

export const lightTheme = {
  colors: {
    primary: '#f6f7f2',
    secondary: '#edece7',
    tertiary: '#eeede8',
    background: '#ffffff',
    hover: '#f4f5f0',
    active: '#f2f3ee',

    text: {
      primary: '#000000',
      secondary: '#666666',
      muted: '#999999',
      accent: '#ffffff',
      link: '#0066cc',
      linkHover: '#004499',
    },

    border: {
      primary: '#eeede8',
      secondary: '#f2f3ee',
      accent: '#0066cc',
    },

    input: {
      background: '#ffffff',
      border: '#eeede8',
      text: '#000000',
      placeholder: '#999999',
    },

    interactive: {
      primary: '#0066cc',
      primaryHover: '#0052a3',
      secondary: '#666666',
      secondaryHover: '#555555',
      success: '#00a651',
      danger: '#e74c3c',
      warning: '#f39c12',
    },

    chart: {
      primary: '#0066cc',
      secondary: '#e74c3c',
      tertiary: '#00a651',
      quaternary: '#f39c12',
      quinary: '#9b59b6',
      senary: '#3498db',
    },

    shadow: {
      sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
      md: '0 2px 4px rgba(0, 0, 0, 0.1)',
      lg: '0 4px 8px rgba(0, 0, 0, 0.15)',
      xl: '0 8px 16px rgba(0, 0, 0, 0.2)',
    },

    code: {
      background: '#f7f8f3',
      border: '#eeede8',
      text: '#000000',
      comment: '#666666',
      keyword: '#0066cc',
      string: '#00a651',
      number: '#f39c12',
    },

    terminal: {
      background: '#1e1e1e',
      text: '#ffffff',
      border: '#333333',
      title: '#cccccc',
    },

    overlay: 'rgba(15, 23, 42, 0.35)',
  },

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    xxxl: '64px',
  },

  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '50%',
  },

  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  typography: {
    fontFamily: {
      primary:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      mono: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    },
    fontSize: {
      xs: '12px',
      sm: '14px',
      base: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px',
      '4xl': '36px',
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.75',
    },
  },

  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
  },
};

// Brand theme (Soquimat — teal / mint / cyan from login)
export const regularTheme = {
  colors: {
    primary: '#f4f8f9',
    secondary: '#e7f0f2',
    tertiary: '#d7e6ea',
    background: '#ffffff',
    hover: '#dff0f2',
    active: '#cfe8ec',

    text: {
      primary: '#0f1c2e',
      secondary: '#4a5d6c',
      muted: '#7a8d9a',
      accent: '#041018',
      link: '#0d8fa0',
      linkHover: '#087484',
    },

    border: {
      primary: '#c9dce2',
      secondary: '#dce8ec',
      accent: '#1aabb8',
    },

    input: {
      background: '#ffffff',
      border: '#c5d8de',
      text: '#0f1c2e',
      placeholder: '#8aa0ab',
    },

    interactive: {
      primary: '#1aabb8',
      primaryHover: '#0d8fa0',
      secondary: '#4a5d6c',
      secondaryHover: '#374858',
      success: '#2f9e6a',
      danger: '#d64545',
      warning: '#d97706',
    },

    chart: {
      primary: '#1aabb8',
      secondary: '#6fe49a',
      tertiary: '#0d8fa0',
      quaternary: '#2f9e6a',
      quinary: '#4a5d6c',
      senary: '#87cfd6',
    },

    shadow: {
      sm: '0 1px 2px rgba(15, 28, 46, 0.06)',
      md: '0 2px 4px rgba(15, 28, 46, 0.08)',
      lg: '0 4px 10px rgba(15, 28, 46, 0.1)',
      xl: '0 8px 18px rgba(15, 28, 46, 0.12)',
    },

    code: {
      background: '#eef6f7',
      border: '#c9dce2',
      text: '#0f1c2e',
      comment: '#4a5d6c',
      keyword: '#0d8fa0',
      string: '#2f9e6a',
      number: '#d97706',
    },

    terminal: {
      background: '#07111f',
      text: '#eef6f7',
      border: '#1a2b3d',
      title: '#a8bcc7',
    },

    overlay: 'rgba(7, 17, 31, 0.45)',
  },

  spacing: lightTheme.spacing,
  borderRadius: lightTheme.borderRadius,
  shadows: lightTheme.shadows,
  typography: lightTheme.typography,
  breakpoints: lightTheme.breakpoints,
  zIndex: lightTheme.zIndex,
};

export const darkTheme = {
  colors: {
    primary: '#1e1e1e',
    secondary: '#2d2d30',
    tertiary: '#3c3c3c',
    background: '#2d2d30',
    hover: '#404040',
    active: '#2d5a87',

    text: {
      primary: '#cccccc',
      secondary: '#999999',
      muted: '#666666',
      accent: '#ffffff',
      link: '#569cd6',
      linkHover: '#4ec9b0',
    },

    border: {
      primary: '#3e3e42',
      secondary: '#464647',
      accent: '#007acc',
    },

    input: {
      background: '#3c3c3c',
      border: '#3e3e42',
      text: '#cccccc',
      placeholder: '#666666',
    },

    interactive: {
      primary: '#007acc',
      primaryHover: '#005a9e',
      secondary: '#6c757d',
      secondaryHover: '#5a6268',
      success: '#4ec9b0',
      danger: '#f48771',
      warning: '#dcdcaa',
    },

    chart: {
      primary: '#007acc',
      secondary: '#f48771',
      tertiary: '#4ec9b0',
      quaternary: '#dcdcaa',
      quinary: '#c586c0',
      senary: '#9cdcfe',
    },

    shadow: {
      sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
      md: '0 2px 4px rgba(0, 0, 0, 0.4)',
      lg: '0 4px 8px rgba(0, 0, 0, 0.5)',
      xl: '0 8px 16px rgba(0, 0, 0, 0.6)',
    },

    code: {
      background: '#2d2d30',
      border: '#3e3e42',
      text: '#cccccc',
      comment: '#6a9955',
      keyword: '#569cd6',
      string: '#ce9178',
      number: '#b5cea8',
    },

    terminal: {
      background: '#0c0c0c',
      text: '#ffffff',
      border: '#3e3e42',
      title: '#cccccc',
    },

    overlay: 'rgba(0, 0, 0, 0.55)',
  },

  spacing: lightTheme.spacing,
  borderRadius: lightTheme.borderRadius,
  shadows: lightTheme.shadows,
  typography: lightTheme.typography,
  breakpoints: lightTheme.breakpoints,
  zIndex: lightTheme.zIndex,
};

/**
 * Tokens de UI unificados para tablas, cards, formularios y chrome.
 * Usar siempre estos valores en lugar de hex locales por componente.
 */
export const getUIColors = (theme) => {
  const c = theme.colors;
  return {
    page: c.primary,
    panel: c.background,
    header: c.secondary,
    section: c.tertiary,
    border: c.border.primary,
    borderStrong: c.border.secondary,
    borderAccent: c.border.accent,
    text: c.text.primary,
    textSecondary: c.text.secondary,
    muted: c.text.muted,
    textOnAccent: c.text.accent,
    link: c.text.link,
    rowHover: c.hover,
    active: c.active,
    inputBg: c.input.background,
    inputBorder: c.input.border,
    inputText: c.input.text,
    inputPlaceholder: c.input.placeholder,
    interactive: c.interactive.primary,
    interactiveHover: c.interactive.primaryHover,
    danger: c.interactive.danger,
    success: c.interactive.success,
    warning: c.interactive.warning,
    overlay: c.overlay,
    shadowSm: c.shadow.sm,
    shadowMd: c.shadow.md,
    // aliases usados por forms/drawers legacy
    title: c.text.primary,
    panelBorder: c.border.primary,
    sectionBg: c.tertiary,
    sectionBorder: c.border.primary,
  };
};

const THEMES_BY_MODE = {
  light: lightTheme,
  regular: regularTheme,
  dark: darkTheme,
};

/**
 * Resolve theme from mode string or legacy boolean.
 * boolean false → active non-dark mode (light or regular).
 */
export const getTheme = (modeOrDark = undefined) => {
  if (typeof modeOrDark === 'string') {
    return THEMES_BY_MODE[normalizeThemeMode(modeOrDark)] || lightTheme;
  }
  if (modeOrDark === true) {
    return darkTheme;
  }
  if (_activeThemeMode === 'dark') {
    return darkTheme;
  }
  return THEMES_BY_MODE[_activeThemeMode] || lightTheme;
};

export const theme = lightTheme;
